import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";
import {
  AlertTriangle,
  AlignLeft,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Flag,
  Inbox,
  Mail,
  Plus,
  Tag,
} from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";
import {
  HERO_VORGANG_MUTED_BLOCK_BG,
  HERO_VORGANG_RAIL_WIDTH_PX,
} from "./hero-vorgang-tokens";

export type HeroVorgangPickerField =
  | "object"
  | "due"
  | "tags"
  | "priority"
  | "description";

export type HeroVorgangRailData = {
  readonly externalId: string;
  readonly title: string;
  readonly statusLabel: string;
  readonly assignee: {
    readonly initials: string;
    readonly name: string;
    readonly bg: string;
  } | null;
  readonly board: {
    readonly name: string;
    readonly icon: "inbox" | "alert";
  } | null;
  readonly recipient: {
    readonly name: string;
    readonly source: string;
  } | null;
  readonly property: {
    readonly name: string;
    readonly unit?: string;
  } | null;
  readonly openPickers: readonly HeroVorgangPickerField[];
};

function SectionLabel(props: { readonly children: ReactNode }): ReactNode {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: ld("rgba(11,15,25,0.40)", "rgba(250,250,250,0.4)"),
        marginBottom: 6,
      }}
    >
      {props.children}
    </div>
  );
}

function MutedBlock(props: { readonly children: ReactNode }): ReactNode {
  return (
    <div
      style={{
        background: HERO_VORGANG_MUTED_BLOCK_BG,
        borderRadius: 12,
        padding: "10px 12px",
      }}
    >
      {props.children}
    </div>
  );
}

function pickerMeta(
  t: Translate,
): Record<
  HeroVorgangPickerField,
  { readonly icon: typeof Building2; readonly label: string }
> {
  return {
    object: { icon: Building2, label: t("objekt") },
    due: { icon: CalendarDays, label: t("faellig") },
    tags: { icon: Tag, label: t("tags") },
    priority: { icon: Flag, label: t("prioritaet") },
    description: { icon: AlignLeft, label: t("beschreibung") },
  };
}

function PickerRow(props: {
  readonly field: HeroVorgangPickerField;
}): ReactNode {
  const t = useTranslations("marketing.heroVorgangDetailRail");
  const meta = pickerMeta(t)[props.field];
  const Icon = meta.icon;
  return (
    <div
      className="mkhero-vorgang-picker-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: 28,
        padding: "2px 4px",
        borderRadius: 6,
        cursor: "default",
      }}
    >
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          border: `1.5px dashed ${ld("rgba(11,15,25,0.22)", "rgba(250,250,250,0.22)")}`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
        }}
      >
        <Icon width={12} height={12} strokeWidth={1.75} />
      </span>
      <span
        style={{ fontSize: 11, fontWeight: 500, color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)") }}
      >
        {meta.label}
      </span>
    </div>
  );
}

export function HeroVorgangDetailRail(props: {
  readonly rail: HeroVorgangRailData;
}): ReactNode {
  const t = useTranslations("marketing.heroVorgangDetailRail");
  const rail: HeroVorgangRailData = props.rail;

  return (
    <div
      style={{
        width: HERO_VORGANG_RAIL_WIDTH_PX,
        flexShrink: 0,
        borderLeft: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#fff", "#181b1f"),
        padding: "14px 16px",
        boxSizing: "border-box",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontFamily: H.label,
            fontSize: 11,
            color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
          }}
        >
          {rail.externalId}
        </span>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#3A9E6B",
          }}
        />
      </div>

      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: ld("#0B0F19", "#fafafa"),
          lineHeight: 1.35,
          marginBottom: 10,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {rail.title}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
          borderRadius: 9,
          padding: "9px 11px",
          background: ld("#fff", "#181b1f"),
          boxShadow: `0 1px 2px ${ld("rgba(11,15,25,0.04)", "rgba(0,0,0,0.11)")}`,
          marginBottom: 12,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12.5,
            fontWeight: 700,
            color: ld("#0B0F19", "#fafafa"),
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              border: `2px solid ${ld("#C2C7CE", "#505458")}`,
              boxSizing: "border-box",
            }}
          />
          {rail.statusLabel}
        </span>
        <span style={{ color: ld("rgba(11,15,25,0.35)", "rgba(250,250,250,0.35)"), display: "inline-flex" }}>
          <ChevronRight width={15} height={15} strokeWidth={1.8} />
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rail.assignee !== null ? (
          <MutedBlock>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: ld("rgba(11,15,25,0.40)", "rgba(250,250,250,0.4)"),
                }}
              >
                {t("zustaendig")}
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  lineHeight: 1.2,
                }}
              >
                {t("follower")}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: rail.assignee.bg,
                  color: ld("#fff", "#181b1f"),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {rail.assignee.initials}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: ld("#0B0F19", "#fafafa"),
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {rail.assignee.name}
              </span>
            </div>
          </MutedBlock>
        ) : null}

        {rail.board !== null ? (
          <MutedBlock>
            <SectionLabel>{t("thema")}</SectionLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  color: rail.board.icon === "alert" ? ld("#B65C5C", "#eb8c8a") : ld("#6B7280", "#a6aaaf"),
                  display: "inline-flex",
                }}
              >
                {rail.board.icon === "alert" ? (
                  <AlertTriangle width={15} height={15} strokeWidth={1.9} />
                ) : (
                  <Inbox width={15} height={15} strokeWidth={1.9} />
                )}
              </span>
              <span style={{ fontSize: 13, color: ld("#0B0F19", "#fafafa"), flex: 1 }}>
                {rail.board.name}
              </span>
              <ChevronDown
                width={14}
                height={14}
                strokeWidth={1.8}
                color={ld("rgba(11,15,25,0.3)", "rgba(250,250,250,0.3)")}
              />
            </div>
          </MutedBlock>
        ) : null}

        {rail.recipient !== null ? (
          <MutedBlock>
            <SectionLabel>{t("empfaenger")}</SectionLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{ color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"), display: "inline-flex" }}
              >
                <Mail width={14} height={14} strokeWidth={1.8} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ fontSize: 13, fontWeight: 500, color: ld("#0B0F19", "#fafafa") }}
                >
                  {rail.recipient.name}
                </div>
                <div style={{ fontSize: 11, color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)") }}>
                  {rail.recipient.source}
                </div>
              </div>
              <Plus
                width={14}
                height={14}
                strokeWidth={1.8}
                color={ld("rgba(11,15,25,0.35)", "rgba(250,250,250,0.35)")}
              />
            </div>
          </MutedBlock>
        ) : null}

        {rail.property !== null ? (
          <MutedBlock>
            <SectionLabel>{t("liegenschaft")}</SectionLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: H.accentSoft,
                  color: H.accent,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Building2 width={15} height={15} strokeWidth={1.9} />
              </span>
              <div>
                <div
                  style={{ fontSize: 12.5, fontWeight: 600, color: ld("#0B0F19", "#fafafa") }}
                >
                  {rail.property.name}
                </div>
                {rail.property.unit !== undefined ? (
                  <div style={{ fontSize: 11, color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)") }}>
                    {rail.property.unit}
                  </div>
                ) : null}
              </div>
            </div>
          </MutedBlock>
        ) : null}
      </div>

      {rail.openPickers.length > 0 ? (
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {rail.openPickers.map((field: HeroVorgangPickerField) => (
            <PickerRow key={field} field={field} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
